// src/services/adminService.ts

import { apiService } from './api_url';

// Список администраторов из переменной окружения
const ADMIN_IDS = (process.env.REACT_APP_ADMIN_IDS || process.env.NEXT_PUBLIC_ADMIN_IDS || '').split(',').map(id => id.trim()).filter(id => id);

export interface AdminStats {
    total_payments: { count: number; amount: number };
    active_subscriptions: number;
    total_users: number;
    churned_last_30d: number;
    monthly_payments: Array<{ month: string; count: number; amount: number }>;
    new_users_growth: Array<{ month: string; new_users: number }>;
}

interface BackendAdminStats {
    total_payments: { count: number; amount: number };
    active_subscriptions: number;
    total_users: number;
    churned_last_30d: number;
    monthly_payments: Array<{ month: string; count: number; amount: number }>;
    subscriptions_growth?: Array<{ month: string; new_users: number }>;
    new_users_growth?: Array<{ month: string; new_users: number }>;
}

export interface ReferralDetail {
    user_id: string;
    username: string | null;
    created_at: string | null;
    purchase_amount: number;
    earned: number;
    has_payment: boolean;
}

export interface ReferralStats {
    referrer_id: string;
    username: string | null;
    total_referrals: number;
    paying_referrals: number;
    total_amount: number;
    total_earned: number;
    conversion_rate: number;
    referrals: ReferralDetail[];
}

export interface AdminCheckResult {
    isAdmin: boolean;
    telegramId: string | null;
    error?: string;
}

export interface PaymentRecord {
    id: string;
    user_id: string;
    amount: number;
    label_sub: string;
    payment_time: string;
    status: string;
    username: string | null;
    email: string | null;
}

export interface MonthlyStats {
    month: string;
    count: number;
    total: number;
    unique_users: number;
}

class AdminService {
    private _isTelegram: boolean = false;
    private _telegramUserId: string | null = null;
    private _initData: string | null = null;

    /**
     * Установка контекста Telegram (вызывается в AdminPage)
     */
    setTelegramContext(isTelegram: boolean, userId: string | null, initData: string | null = null) {
        this._isTelegram = isTelegram;
        this._telegramUserId = userId;
        this._initData = initData;
    }

    /**
     * Проверяет, является ли пользователь администратором
     */
    isUserAdmin(telegramId: string): boolean {
        return ADMIN_IDS.includes(telegramId);
    }

    /**
     * Получает список ID администраторов
     */
    getAdminIds(): string[] {
        return [...ADMIN_IDS];
    }

    /**
     * Вспомогательный метод: создает конфиг с заголовком X-Telegram-Id если в Telegram
     */
    private getAuthConfig(): any {
        const headers: Record<string, string> = {};

        if (this._isTelegram && this._telegramUserId) {
            headers['X-Telegram-Id'] = this._telegramUserId;

            if (this._initData) {
                headers['X-Telegram-Init-Data'] = this._initData;
            }
        }

        return { headers };
    }

    /**
     * Проверяет авторизацию и права администратора
     */
    async checkAdminAccess(): Promise<AdminCheckResult> {
        // Проверяем через Telegram контекст
        if (this._isTelegram && this._telegramUserId) {
            const isAdmin = this.isUserAdmin(this._telegramUserId);

            if (!isAdmin) {
                return {
                    isAdmin: false,
                    telegramId: this._telegramUserId,
                    error: 'Доступ запрещен. Эта страница только для администраторов.'
                };
            }

            return {
                isAdmin: true,
                telegramId: this._telegramUserId
            };
        }

        // Для сайта - проверяем через сервер
        console.log('🔍 AdminService: проверка через сервер');
        try {
            const authResult = await apiService.checkAuth();

            if (authResult.status !== 'authenticated') {
                return {
                    isAdmin: false,
                    telegramId: null,
                    error: 'Необходима авторизация'
                };
            }

            const telegramId = authResult.user.telegram_id;
            const isAdmin = this.isUserAdmin(telegramId);

            if (!isAdmin) {
                return {
                    isAdmin: false,
                    telegramId,
                    error: 'Доступ запрещен. Эта страница только для администраторов.'
                };
            }

            return {
                isAdmin: true,
                telegramId
            };

        } catch (err: any) {
            return {
                isAdmin: false,
                telegramId: null,
                error: err.message || 'Ошибка проверки доступа'
            };
        }
    }

    /**
     * Получает статистику для админов
     */
    async getStats(): Promise<AdminStats | null> {
        try {
            const config = this.getAuthConfig();

            const response = await apiService.getAdminStats(config);
            if (response.success) {
                const backendStats = response.stats as BackendAdminStats;
                const newUsersGrowth = backendStats.new_users_growth || backendStats.subscriptions_growth || [];

                return {
                    total_payments: backendStats.total_payments,
                    active_subscriptions: backendStats.active_subscriptions,
                    total_users: backendStats.total_users,
                    churned_last_30d: backendStats.churned_last_30d,
                    monthly_payments: backendStats.monthly_payments,
                    new_users_growth: newUsersGrowth
                };
            }
            return null;
        } catch (err) {
            console.error('❌ Ошибка загрузки статистики:', err);
            return null;
        }
    }

    /**
     * Получает реферальную статистику для админов
     */
    async getReferralStats(): Promise<ReferralStats[]> {
        try {
            const config = this.getAuthConfig(); // ✅ Добавляем конфиг

            const response = await apiService.getAdminReferralStats(config);
            if (response.success) {
                return response.referral_stats;
            }
            return [];
        } catch (err) {
            console.error('❌ Ошибка загрузки реферальной статистики:', err);
            return [];
        }
    }

    /**
     * Получает все успешные платежи пользователей
     */
    async getAllPayments(limit: number = 100, offset: number = 0): Promise<{
        payments: PaymentRecord[];
        total: number;
        monthlyStats: MonthlyStats[];
    }> {
        try {
            const config = this.getAuthConfig(); // ✅ Добавляем конфиг

            const response = await apiService.getAdminAllPayments(limit, offset, config); // ✅ Передаем конфиг
            if (response.success) {
                return {
                    payments: response.payments,
                    total: response.total,
                    monthlyStats: response.monthly_stats
                };
            }
            return { payments: [], total: 0, monthlyStats: [] };
        } catch (err) {
            console.error('❌ Ошибка загрузки платежей:', err);
            return { payments: [], total: 0, monthlyStats: [] };
        }
    }
}

export const adminService = new AdminService();